import { useEffect, useId, useRef, useState } from "react";
import type { LeadIntakeMetaPagesResponse, LeadIntakeSettingsResponse } from "@gioia365/contracts";
import { ApiRequestError } from "../../api/http";
import { useTab } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { RadioGroup } from "../../design/components/RadioGroup";
import { Reason } from "../../design/components/Reason";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import { metaNeedsReconnect, metaRoundText, openMetaPagesLogin, preloadMetaPagesSdk } from "./lead-intake-meta";
import { useConnectLeadIntakeMeta, useDisconnectLeadIntakeMeta, useLeadIntakeMetaPages } from "./lead-intake-queries";

// Il riquadro «Moduli di Meta» di Impostazioni → «Ingressi dei Lead» (piano del 12 ottobre 2026, I3, D1; UX-7):
// - senza Meta sul server dice «Il collegamento con Meta non è attivo su questo server.» e non ha pulsanti;
// - senza pagina, «Collega la pagina Facebook» (soltanto con `settings.lead_intake`): l'accesso di Facebook (`lead-intake-meta.ts`),
//   le pagine che la persona amministra e la finestra «Scegli la pagina» con «Collega questa pagina»; il gettone breve resta soltanto
//   in memoria, finché la finestra è aperta;
// - con la pagina: quale, da quando, l'ultima lettura dei moduli (riuscita, non riuscita, o «Meta non accetta più il collegamento:
//   collega di nuovo la pagina.»), «Collega di nuovo la pagina» e «Scollega la pagina» con la domanda.
// Ogni esito resta nel riquadro o nella sua finestra, con i testi dei cataloghi, mai in un avviso a comparsa.

type Status = { tone: "info" | "warning"; text: string };
type Choosing = { pages: LeadIntakeMetaPagesResponse["pages"]; selected: string; error: string | null };

const isCode = (error: unknown, code: string) => error instanceof ApiRequestError && error.code === code;

export function MetaBox({ base }: { base: LeadIntakeSettingsResponse }) {
  const t = useT();
  const id = useId();
  const tab = useTab();
  const access = useAccess();
  const { locale } = useLocale();
  const pages = useLeadIntakeMetaPages(tab);
  const connect = useConnectLeadIntakeMeta(tab);
  const disconnect = useDisconnectLeadIntakeMeta(tab);
  const meta = base.meta;
  const editable = base.canEdit;
  const timeZone = access.context.settings.timeZone;
  const appId = meta.sdk?.appId ?? null;
  const graphVersion = meta.sdk?.graphVersion ?? null;
  // Il gettone breve dell'accesso di Facebook, soltanto finché la finestra «Scegli la pagina» è aperta.
  const tokenRef = useRef<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [choosing, setChoosing] = useState<Choosing | null>(null);
  const [asking, setAsking] = useState<{ error: string | null } | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const busy = opening || pages.isPending || connect.isPending || disconnect.isPending;

  // L'SDK di Facebook si chiede soltanto qui, appena il pulsante si vede: così la finestra di Facebook si apre dentro il clic.
  useEffect(() => {
    if (!editable || appId === null || graphVersion === null) return;
    preloadMetaPagesSdk({ appId, graphVersion, locale });
  }, [editable, appId, graphVersion, locale]);

  function closeChoice() {
    tokenRef.current = null;
    setChoosing(null);
  }

  async function startConnect() {
    if (appId === null || graphVersion === null || busy) return;
    setStatus(null);
    setOpening(true);
    try {
      const outcome = await openMetaPagesLogin({ appId, graphVersion, locale });
      if (outcome.kind === "cancelled") {
        setStatus({ tone: "info", text: t("leadIntakeSettings.metaCancelled") });
        return;
      }
      if (outcome.kind === "failed") {
        setStatus({ tone: "warning", text: t(outcome.reason === "permissions" ? "leadIntakeSettings.metaPermissions" : "leadIntakeSettings.metaSdkUnavailable") });
        return;
      }
      const found = (await pages.mutateAsync(outcome.accessToken)).pages;
      const first = found.find((page) => page.id === meta.page?.id) ?? found[0];
      if (!first) {
        setStatus({ tone: "warning", text: t("leadIntakeSettings.metaNoPages") });
        return;
      }
      tokenRef.current = outcome.accessToken;
      setChoosing({ pages: found, selected: first.id, error: null });
    } catch (error) {
      setStatus({ tone: "warning", text: messageForError(t, error) });
    } finally {
      setOpening(false);
    }
  }

  async function confirmConnect() {
    const token = tokenRef.current;
    if (choosing === null || token === null) return;
    setChoosing({ ...choosing, error: null });
    try {
      await connect.mutateAsync({ accessToken: token, pageId: choosing.selected, expectedVersion: meta.version });
      closeChoice();
      setStatus({ tone: "info", text: t("leadIntakeSettings.metaConnectedDone") });
    } catch (error) {
      if (isCode(error, "version_conflict")) {
        // Il collegamento è cambiato intanto: la finestra si chiude e il riquadro, riletto, lo dice.
        closeChoice();
        setStatus({ tone: "warning", text: t("leadIntakeSettings.metaConflict") });
        return;
      }
      setChoosing({ ...choosing, error: messageForError(t, error) });
    }
  }

  async function confirmDisconnect() {
    if (meta.version === null) return;
    try {
      await disconnect.mutateAsync(meta.version);
      setAsking(null);
      setStatus({ tone: "info", text: t("leadIntakeSettings.metaDisconnected") });
    } catch (error) {
      if (isCode(error, "version_conflict")) {
        setAsking(null);
        setStatus({ tone: "warning", text: t("leadIntakeSettings.metaConflict") });
        return;
      }
      setAsking({ error: messageForError(t, error) });
    }
  }

  if (!meta.available) {
    return (
      <section className="g-intake__box" aria-labelledby={`${id}-title`}>
        <h2 id={`${id}-title`}>{t("leadIntakeSettings.meta")}</h2>
        <Reason>{t("leadIntakeSettings.metaUnavailable")}</Reason>
      </section>
    );
  }

  const round = metaRoundText(t, timeZone, meta);
  const reconnect = meta.connected && metaNeedsReconnect(meta);
  return (
    <section className="g-intake__box" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t("leadIntakeSettings.meta")}</h2>
      <p className="g-intake__muted">{t("leadIntakeSettings.metaText")}</p>
      {meta.connected && meta.page ? (
        <>
          <p>{t("leadIntakeSettings.metaConnected", { page: meta.page.name, when: meta.connectedAt === null ? "" : formatNumericDateTime(timeZone, meta.connectedAt) })}</p>
          <p className="g-intake__status" data-tone={round.tone} role="status">
            {round.text}
          </p>
        </>
      ) : (
        <p className="g-intake__muted">{t("leadIntakeSettings.metaNone")}</p>
      )}
      <p className="g-intake__muted">{t("leadIntakeSettings.metaRules")}</p>
      {editable ? (
        <>
          <p className="g-intake__muted">{t("leadIntakeSettings.metaConnectHint")}</p>
          <div className="g-intake__actions">
            <Button variant={!meta.connected || reconnect ? "primary" : "secondary"} disabled={busy} aria-busy={opening || pages.isPending ? "true" : undefined} onClick={() => void startConnect()}>
              {t(meta.connected ? "leadIntakeSettings.metaReconnect" : "leadIntakeSettings.metaConnect")}
            </Button>
            {meta.connected ? (
              <Button disabled={busy} onClick={() => setAsking({ error: null })}>
                {t("leadIntakeSettings.metaDisconnect")}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
      {status ? (
        <p className="g-intake__status" data-tone={status.tone} role="status">
          {status.text}
        </p>
      ) : null}

      <Dialog
        open={choosing !== null}
        title={t("leadIntakeSettings.metaChooseTitle")}
        onClose={() => {
          if (!connect.isPending) closeChoice();
        }}
        footer={
          <>
            <Button onClick={closeChoice} disabled={connect.isPending}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={() => void confirmConnect()} disabled={connect.isPending} aria-busy={connect.isPending ? "true" : undefined}>
              {t("leadIntakeSettings.metaChooseConfirm")}
            </Button>
          </>
        }
      >
        <p>{t("leadIntakeSettings.metaChooseText", { organization: access.context.organization.name })}</p>
        <RadioGroup
          name={`${id}-page`}
          legend={t("leadIntakeSettings.metaChooseLegend")}
          hideLegend
          options={(choosing?.pages ?? []).map((page) => ({ value: page.id, label: page.name }))}
          value={choosing?.selected ?? null}
          disabled={connect.isPending}
          onChange={(selected) => {
            if (choosing) setChoosing({ ...choosing, selected, error: null });
          }}
        />
        {choosing?.error ? (
          <Reason tone="warning" role="alert">
            {choosing.error}
          </Reason>
        ) : null}
      </Dialog>

      <Dialog
        open={asking !== null}
        title={t("leadIntakeSettings.metaDisconnectTitle")}
        onClose={() => {
          if (!disconnect.isPending) setAsking(null);
        }}
        footer={
          <>
            <Button onClick={() => setAsking(null)} disabled={disconnect.isPending}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={() => void confirmDisconnect()} disabled={disconnect.isPending} aria-busy={disconnect.isPending ? "true" : undefined}>
              {t("leadIntakeSettings.metaDisconnectConfirm")}
            </Button>
          </>
        }
      >
        <p>{t("leadIntakeSettings.metaDisconnectText", { page: meta.page?.name ?? "" })}</p>
        {asking?.error ? (
          <Reason tone="warning" role="alert">
            {asking.error}
          </Reason>
        ) : null}
      </Dialog>
    </section>
  );
}
